
import React, { useEffect, useContext } from 'react';
import styles from './Modal.module.css';
import logoImage from '../Assets/prawnhublogowhite.png';
import { useModal, MODAL_TYPES } from '../Context/ModalContext';
import { SoundContext } from '../Context/SoundContext';
import { getRandomQuote } from '../Data/wisdomQuotes';
import NewsletterForm from './NewsletterForm';
import ErrorBoundary from './ErrorBoundary';
import { trackEvent } from '../Utils/analytics';

import { useSmartPreload, useInvasiveAssetsPreload } from '../Hooks/useSmartPreload';
// (This hook must be inside the Modal component, not at the top level)


function SoundSettingsContent() {

  const {
    filterVolume,
    setFilterVolume,
    pumpVolume,
    setPumpVolume,
    soundFxVolume,
    setSoundFxVolume,
    playSoundFx
  } = useContext(SoundContext);
  const sliderStyle = { accentColor: '#2596be' };

  // Throttled auditory feedback for Sound FX slider
  const lastFeedbackTime = React.useRef(0);
  const feedbackThrottleMs = 150; // Play feedback every 150ms max during drag

  // Play feedback at the given volume, throttled
  const provideSoundFxFeedback = React.useCallback((volume) => {
    const now = Date.now();
    if (volume > 0 && now - lastFeedbackTime.current >= feedbackThrottleMs) {
      // Pass explicit volume override for feedback
      playSoundFx('click', { volume });
      lastFeedbackTime.current = now;
    }
  }, [playSoundFx, feedbackThrottleMs]);

  // For onChange: update volume, then play feedback at new value
  const handleSoundFxSliderChange = React.useCallback((e) => {
    const newVal = Number(e.target.value);
    setSoundFxVolume(newVal, () => provideSoundFxFeedback(newVal));
  }, [setSoundFxVolume, provideSoundFxFeedback]);


  // iOS detection
  const isIOS = typeof navigator !== 'undefined' && /iPad|iPhone|iPod/.test(navigator.userAgent);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: '1.1em',
        marginTop: '1.2em',
        alignItems: 'center',
        width: '100%',
        marginBottom: '2.2em',
        overflowX: 'hidden', // Fix mobile overflow issue
        
      }}
    >
      {isIOS && (
        <div
          style={{
            textAlign: 'center',
            color: '#2596be',
            fontWeight: 500,
            fontSize: '0.9em',
            maxWidth: 320,
            marginBottom: '0.5em',
          }}
        >
          No sound? Turn off silent mode on your device
        </div>
      )}
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          width: 320,
          maxWidth: '90vw',
          background: 'rgba(34,34,34,0.12)',
          borderRadius: 8,
          padding: '6px 18px',
        }}
      >
        <label htmlFor="filter-volume" style={{ minWidth: 90, textAlign: 'right' }}>Filter:</label>
        <input
          type="range"
          id="filter-volume"
          min="0"
          max="100"
          step="1"
          value={filterVolume}
          onChange={e => setFilterVolume(Number(e.target.value))}
          style={{ ...sliderStyle, flex: 1, maxWidth: 140 }}
        />
        <span style={{ marginLeft: 8, minWidth: 28, textAlign: 'right' }}>{filterVolume}%</span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          width: 320,
          maxWidth: '90vw',
          background: 'rgba(34,34,34,0.12)',
          borderRadius: 8,
          padding: '6px 18px',
        }}
      >
        <label htmlFor="tank-volume" style={{ minWidth: 90, textAlign: 'right' }}>Tank:</label>
        <input
          type="range"
          id="tank-volume"
          min="0"
          max="100"
          step="1"
          value={pumpVolume}
          onChange={e => setPumpVolume(Number(e.target.value))}
          style={{ ...sliderStyle, flex: 1, maxWidth: 140 }}
        />
        <span style={{ marginLeft: 8, minWidth: 28, textAlign: 'right' }}>{pumpVolume}%</span>
      </div>
      <div
        style={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          gap: 10,
          width: 320,
          maxWidth: '90vw',
          background: 'rgba(34,34,34,0.12)',
          borderRadius: 8,
          padding: '6px 18px',
        }}
      >
        <label htmlFor="soundfx-volume" style={{ minWidth: 90, textAlign: 'right' }}>Sound FX:</label>
        <input
          type="range"
          id="soundfx-volume"
          min="0"
          max="100"
          step="1"
          value={soundFxVolume}
          onChange={handleSoundFxSliderChange}
          onInput={e => provideSoundFxFeedback(Number(e.target.value))}
          style={{ ...sliderStyle, flex: 1, maxWidth: 140 }}
        />
        <span style={{ marginLeft: 8, minWidth: 28, textAlign: 'right' }}>{soundFxVolume}%</span>
      </div>
    </div>
  );
}

const modalConfig = {
  [MODAL_TYPES.SOUND_SETTINGS]: {
    title: 'Sound Settings',
    buttons: [
      { label: 'Reset', action: 'resetVolumes' },
      { label: 'Close', action: 'close' },
    ],
  },
  SoundSettings: {
    title: 'Sound Settings',
    buttons: [
      { label: 'Reset', action: 'resetVolumes' },
      { label: 'Close', action: 'close' },
    ],
  },
  [MODAL_TYPES.INTRO]: {
    title: 'This is a prawn website',
    content: (
      <>
        <p>Please don't disturb them, they're just vibing.</p>
      </>
    ),
    buttons: [
      { label: 'I can\'t vibe', action: 'goKillModal' },
      { label: 'I can vibe', action: 'goVibe' },
    ],
  },
  [MODAL_TYPES.KILL]: {
    title: 'Help the Prawns',
    content: <p>If you wanna make yourself useful, you can kill the shit messing with the prawns' vibe</p>,
    buttons: [
      { label: 'Back', action: 'goIntro' },
      { label: 'Start', action: 'goKillSimulator' },
    ],
  },
  [MODAL_TYPES.PUNISHMENT]: {
    title: 'You had one job!',
    content: <p>You were supposed to just vibe...</p>,
    buttons: [
      { label: 'I can\'t vibe', action: 'goKillModal' },
      { label: 'I can vibe', action: 'goVibe' },
    ],
  },
  [MODAL_TYPES.GAME_LOST]: {
    title: 'Damn.',
    content: <p>You ended the prawns' bloodline. You were supposed to protect them.</p>,
    buttons: [
      { label: 'I just wanna vibe', action: 'goVibe' },
      { label: 'Try again', action: 'goKillSimulator' },
    ],
  },
  [MODAL_TYPES.CONGRATS]: {
    title: 'Wow!',
    content: ({ time, handleTweetClick, playClickSound }) => {
      // Parse the time value, which is in format "minutes:seconds:milliseconds"
      let minutes = 0;
      let seconds = 0;
      let milliseconds = 0;
      
      // Extract time components
      if (typeof time === 'string') {
        const timeParts = time.split(':');
        if (timeParts.length === 3) {
          minutes = parseInt(timeParts[0], 10);
          seconds = parseInt(timeParts[1], 10);
          milliseconds = parseInt(timeParts[2], 10);
        }
      }
      
      // Calculate total seconds for comparison
      const totalSeconds = minutes * 60 + seconds + (milliseconds / 1000);
      
      // Format the time based on requirements
      let formattedTime;
      if (totalSeconds < 61) {
        // Format as x.xxs if under 61 seconds
        formattedTime = `${seconds}.${milliseconds.toString().padStart(2, '0')}s`;
      } else {
        // Format as xm x.xxs if 61 seconds or over
        formattedTime = `${minutes}m ${seconds}.${milliseconds.toString().padStart(2, '0')}s`;
      }
      
      // Determine which message to show based on the time
      let messageContent;
      if (totalSeconds < 18) {
        messageContent = `${formattedTime} is an unreal time! You're a prawnstar...`;
      } else if (totalSeconds >= 60) {
        messageContent = `Protecting the vibe ain't easy. You scored a time of ${formattedTime}`;
      } else {
        messageContent = `You protected the vibe and it only took ${formattedTime}`;
      }
      
      return (
        <>
          <p>{messageContent}</p>
          <p>
            If you want, you can{' '}
            <button
              type="button"
              className="tweet-link"
              style={{
                cursor: 'pointer',
                background: 'none',
                border: 'none',
                padding: 0,
                color: '#51abcb', // teal theme color
                textDecoration: 'underline',
                textDecorationColor: '#51abcb',
                font: 'inherit',
                fontSize: 'inherit',
              }}
              onClick={e => {
                if (playClickSound) playClickSound();
                if (handleTweetClick) handleTweetClick();
              }}
            >
              tweet your time
            </button>
          </p>
        </>
      );
    },
    buttons: [
      { label: 'Back to vibing', action: 'goVibe' },
      { label: 'Play again', action: 'goKillSimulator' },
    ],
  },
    [MODAL_TYPES.ABOUT]: {
      title: 'WTF?',
      content: ({ handleLabbyLabsClick, showNewsletterForm, setShowNewsletterForm, showCharityInfo, setShowCharityInfo, playClickSound }) => {
        // Import icon images
        const discordIcon = require('../Assets/discord-icon-white.png');
        const xIcon = require('../Assets/x-icon-white.png');
        const newsletterIcon = require('../Assets/newsletter-icon-white.png');
        const charityIcon = require('../Assets/charity-icon-white.png');
        return (
          <>
            <p className="about-main-desc">
        PrawnHub is a satirical prawn website by{' '}
        <a
          href="https://labbylabs.xyz"
          target="_blank"
          rel="noopener noreferrer"
          className={styles.external + ' labby-labs-link'}
          aria-label="Visit LabbyLabs.xyz (opens in new tab)"
          title="Visit LabbyLabs.xyz"
          onClick={() => { trackEvent('external_link', { destination: 'labby_labs', source: 'about_modal_inline' }); if (playClickSound) playClickSound(); }}
          style={{
            color: '#51abcb', // lighter teal default
            textDecoration: 'none',
            border: 'none',
            fontWeight: 500,
            padding: 0,
            cursor: 'pointer',
          }}
        >
          <span className="labby-labs-underline" style={{
            borderBottom: '2px solid #51abcb',
            textDecoration: 'none',
            paddingBottom: 1,
            transition: 'border-color 0.15s',
          }}>
            Labby Labs
          </span>
        </a>
            </p>
            {/* External links row */}
            <div
              style={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'center',
                alignItems: 'flex-end',
                gap: '1.5em',
                margin: showNewsletterForm ? '0 0 0 0' : '0 0 1.3em 0',
                width: '100%',
              }}
            >
               <style>{`
                  .about-icon-link {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    width: 52px;
                    height: 52px;
                    border-radius: 12px;
                    background: transparent; /* fully invisible */
                    transition: background 0.15s;
                    text-decoration: none;
                    cursor: pointer !important;
                    -webkit-tap-highlight-color: transparent;
                    touch-action: manipulation;
                  }
                  
                  /* Only apply hover effects on devices that support true hover */
                  @media (hover: hover) and (pointer: fine) {
                    .about-icon-link:hover {
                      background: rgba(220,220,220,0.13); /* subtle gray */
                      cursor: pointer !important;
                    }
                    .about-icon-link:hover img {
                      cursor: pointer !important;
                    }
                  }
                  
                  /* Mobile-specific active state for touch feedback */
                  @media (hover: none) and (pointer: coarse) {
                    .about-icon-link:active {
                      background: rgba(220,220,220,0.13); /* brief flash on tap */
                      transition: background 0.1s;
                    }
                    .about-icon-link {
                      background: transparent !important; /* ensure no stuck hover states */
                    }
                  }
                  
                  .about-icon-link img {
                    width: 32px;
                    height: 32px;
                    display: block;
                    cursor: pointer !important;
                  }
                  .disabled-icon:hover img { cursor: default !important; }
                  .disabled-icon img { opacity: 0.5; }
                  @media (hover: none) and (pointer: coarse) {
                    .disabled-icon img {
                      opacity: 0.35;
                      filter: grayscale(50%);
                    }
                  }
                  .about-icon-label {
                    font-size: 0.68em;
                    color: #f9f9f9;
                    margin-top: 2px;
                    text-align: center;
                    line-height: 1.1;
                    letter-spacing: 0.01em;
                    user-select: none;
                  }
                  .labby-labs-link:hover {
                    color: #2596be !important; /* darker teal on hover */
                  }
                  .labby-labs-link:hover .labby-labs-underline {
                    border-bottom: 2px solid #2596be !important;
                    cursor: pointer !important;
                  }
                  .labby-labs-link .labby-labs-underline {
                    transition: border-color 0.15s;
                  }
                  .oceanic-society-link:hover {
                    color: #2596be !important; /* darker teal on hover */
                  }
                  .oceanic-society-link:hover .oceanic-society-underline {
                    border-bottom: 2px solid #2596be !important;
                    cursor: pointer !important;
                  }
                  .oceanic-society-link .oceanic-society-underline {
                    transition: border-color 0.15s;
                  }
               `}</style>
              {/* Discord icon temporarily commented out until Discord is ready */}
              {false && (
                <a
                  href="https://discord.gg/labbylabs"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="about-icon-link"
                  style={{ display: 'flex', alignItems: 'center' }}
                  title="Discord"
                >
                  <img src={discordIcon} alt="Discord" style={{ 
                    width: 38, 
                    height: 38, 
                    display: 'block', 
                    marginTop: 4.5
                  }} />
                </a>
              )}
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <a
            href="https://x.com/labbylabsxyz"
            target="_blank"
            rel="noopener noreferrer"
            className="about-icon-link"
            title="View our X Account"
            onClick={() => { trackEvent('external_link', { destination: 'labby_labs_x', source: 'about_modal' }); if (playClickSound) playClickSound(); }}
            // Removed inline style that could override cursor
          >
            <img src={xIcon} alt="X (Twitter)" />
          </a>
          <span className="about-icon-label">Labby Labs</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button
            onClick={() => {
              if (playClickSound) playClickSound();
              trackEvent('about_newsletter_toggled', { opened: !showNewsletterForm });
              setShowNewsletterForm(!showNewsletterForm);
            }}
            className="about-icon-link"
            title="Subscribe to our newsletter"
            style={{ 
              border: 'none', 
              padding: 0, 
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <img src={newsletterIcon} alt="Newsletter" />
          </button>
          <span className="about-icon-label">Newsletter</span>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          <button
            onClick={() => {
              if (playClickSound) playClickSound();
              trackEvent('about_charity_toggled', { opened: !showCharityInfo });
              setShowCharityInfo(!showCharityInfo);
            }}
            className="about-icon-link"
            title="Learn about our charity partner"
            style={{ 
              border: 'none', 
              padding: 0, 
              cursor: 'pointer',
              outline: 'none'
            }}
          >
            <img src={charityIcon} alt="Charity" />
          </button>
          <span className="about-icon-label">Charity</span>
        </div>
            </div>
            {/* Newsletter form section that appears when icon is clicked */}
            {showNewsletterForm && (
              <div
                style={{
                  width: '100%',
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  margin: '0 0 0.8em 0',
                  opacity: showNewsletterForm ? 1 : 0,
                  transform: showNewsletterForm ? 'translateY(0)' : 'translateY(-10px)',
                  transition: 'all 0.3s ease-out',
                }}
              >
                <div
                  style={{
                    borderRadius: '12px',
                    padding: '0.9em 1.5em 0.4em 1.5em',
                    maxWidth: '320px',
                    width: '100%',
                  }}
                >
                  <p style={{
                    color: '#E6E6E6',
                    fontSize: '0.95em',
                    textAlign: 'center',
                    margin: '0 0 0.8em 0',
                    lineHeight: 1.4,
                    fontWeight: 400,
                  }}>
                    Catch our launches & mischief first
                  </p>
                  <ErrorBoundary>
                    <NewsletterForm 
                      placeholder="Enter your email"
                      style={{ width: '100%' }}
                      mobile={false}
                    />
                  </ErrorBoundary>
                </div>
              </div>
            )}
            {/* Charity info section that appears when icon is clicked */}
            {showCharityInfo && (
              <>
                <div
                  style={{
                    borderTop: '1.5px solid #aaa',
                    margin: '0.7em auto 0.5em auto',
                    width: '82%',
                    maxWidth: '320px',
                    opacity: 0.65,
                    borderRadius: 1,
                    transition: 'width 0.2s',
                  }}
                />
                <p style={{
                  fontSize: '0.98em',
                  color: '#e0e0e0',
                  marginTop: '0.5em',
                  textAlign: 'center',
                  lineHeight: 1.5,
                  letterSpacing: '0.01em',
                  maxWidth: 420,
                  marginLeft: 'auto',
                  marginRight: 'auto',
                  fontWeight: 400,
                  opacity: showCharityInfo ? 1 : 0,
                  transform: showCharityInfo ? 'translateY(0)' : 'translateY(-10px)',
                  transition: 'all 0.3s ease-out',
                }}>
                  Labby Labs likes the{' '}
                  <a
                    href="https://thegivingblock.com/donate/oceanic-society/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.external + ' oceanic-society-link'}
                    aria-label="Donate to Oceanic Society (opens in new tab)"
                    title="Donate to Oceanic Society"
                    onClick={() => { trackEvent('external_link', { destination: 'oceanic_society', source: 'about_modal_inline' }); if (playClickSound) playClickSound(); }}
                    style={{
                      color: '#51abcb', // lighter teal default
                      textDecoration: 'none',
                      border: 'none',
                      fontWeight: 500,
                      padding: 0,
                      cursor: 'pointer',
                    }}
                  >
                    <span className="oceanic-society-underline" style={{
                      borderBottom: '2px solid #51abcb',
                      textDecoration: 'none',
                      paddingBottom: 1,
                      transition: 'border-color 0.15s',
                    }}>
                      Oceanic Society
                    </span>
                  </a>
                  , a nonprofit fighting plastic pollution, unsustainable fishing, habitat destruction, and rising ocean temps.
                </p>
              </>
            )}
          </>
        );
      },
      buttons: [
        { label: 'Back', action: 'goBackToSimulator' },
        { label: 'Visit Labby Labs', action: 'goLabbyLabs' },
      ],
    },
  [MODAL_TYPES.WISDOM]: {
    showLogo: false,
    showTitle: false,
    content: ({ wisdomQuote, setWisdomQuote }) => {
      // wisdomQuote is provided from Modal state, setWisdomQuote can be used if needed
      return (
        <>
          <blockquote className="wisdom-quote-container">
            <p className="wisdom-quote">
              "{wisdomQuote?.text ? (
                wisdomQuote.text.includes('sup@labbylabs.xyz') ? (
                  // Make the email address clickable using a mailto link while preserving the sentence
                  (() => {
                    const parts = wisdomQuote.text.split('sup@labbylabs.xyz');
                    return (
                      <span>
                        {parts[0]}
                        <a
                          href="mailto:sup@labbylabs.xyz"
                          style={{ color: '#51abcb', textDecoration: 'underline' }}
                        >
                          sup@labbylabs.xyz
                        </a>
                        {parts[1]}
                      </span>
                    );
                  })()
                ) : (
                  wisdomQuote.text
                )
              ) : ''}"
            </p>
            <cite className="wisdom-attribution">– {wisdomQuote?.attribution || ''}</cite>
          </blockquote>
        </>
      );
    },
    buttons: [
      { label: 'Back to vibing', action: 'goVibe', primary: true },
    ],
  },
};




const Modal = ({ goToVibe, goToKill, goToIntro }) => {
  const { modal, closeModal, openModal, lastSimulator, resetModal } = useModal();
  const soundCtx = useContext(SoundContext);
  // About modal disclaimers state
  const [showDisclaimers, setShowDisclaimers] = React.useState(false);
  // About modal newsletter form state
  const [showNewsletterForm, setShowNewsletterForm] = React.useState(false);
  // About modal charity info state
  const [showCharityInfo, setShowCharityInfo] = React.useState(false);

  // Wisdom quote state (only for wisdom modal)
  // Use a ref to persist the quote for the session, and useMemo to generate synchronously on open
  const wisdomQuoteRef = React.useRef(null);
  const wisdomQuote = React.useMemo(() => {
    if (modal.isOpen && modal.type === MODAL_TYPES.WISDOM) {
      // Use override quote from props if provided (e.g. easter egg quote)
      if (modal.props?.wisdomQuote) {
        wisdomQuoteRef.current = modal.props.wisdomQuote;
      } else if (!wisdomQuoteRef.current) {
        wisdomQuoteRef.current = getRandomQuote();
      }
      return wisdomQuoteRef.current;
    } else {
      // Reset ref when modal closes or changes type
      wisdomQuoteRef.current = null;
      return null;
    }
  }, [modal.isOpen, modal.type, modal.props]);

  // Smart preloading hooks
  const { preloadModalAssets } = useSmartPreload();
  const { preloadInvasives } = useInvasiveAssetsPreload();

  // Preload SFX and assets when modals open
  useEffect(() => {
    if (modal.isOpen) {
      // Always preload SFX for kill modal
      if (modal.type === MODAL_TYPES.KILL && soundCtx && typeof soundCtx.preloadSfxPhase === 'function') {
        soundCtx.preloadSfxPhase('kill');
        // Preload invasive assets for kill simulator
        preloadInvasives();
      }
      
      // Preload modal-specific assets
      if (modal.type === MODAL_TYPES.KILL || modal.type === MODAL_TYPES.INTRO) {
        preloadModalAssets(modal.type.toLowerCase());
      }
    }
  }, [modal.isOpen, modal.type, soundCtx, preloadModalAssets, preloadInvasives]);


  // No need for useEffect to set wisdom quote anymore

  // Ensure body gets modal-open class when modal is open
  useEffect(() => {
    if (modal.isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [modal.isOpen]);

  // Celebration SFX is now only triggered in game logic to avoid double playback.

  // Reset disclaimers state when modal closes or type changes
  useEffect(() => {
    if (!modal.isOpen || modal.type !== MODAL_TYPES.ABOUT) {
      setShowDisclaimers(false);
      setShowNewsletterForm(false);
      setShowCharityInfo(false);
    }
  }, [modal.isOpen, modal.type]);

  // Don't return null - instead keep modal in DOM and use CSS visibility to prevent CLS
  // if (!modal.isOpen) return null;

  const config = modalConfig[modal.type] || {}; // Provide empty config if none exists
  
  // Handle unknown modal types gracefully (only when modal should be visible)
  if (!modalConfig[modal.type] && modal.isOpen) {
    if (process.env.NODE_ENV === 'development') {
      console.warn('Unknown modal type:', modal.type);
    }
    return (
      <div style={{
        position: 'fixed',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        background: 'rgba(0,0,0,0.9)',
        color: '#fff',
        padding: '20px',
        borderRadius: '8px',
        textAlign: 'center',
        zIndex: 10000,
        // CLS prevention
        willChange: 'transform, opacity',
        contain: 'layout style paint',
      }}>
        <h3>Navigation Error</h3>
        <p>Unknown screen type. Returning to start.</p>
        <button 
          onClick={() => {
            if (resetModal) resetModal();
            if (goToIntro) goToIntro();
          }}
          style={{
            background: '#2596be',
            color: 'white',
            border: 'none',
            padding: '8px 16px',
            borderRadius: '4px',
            cursor: 'pointer',
            marginTop: '10px'
          }}
        >
          Go Home
        </button>
      </div>
    );
  }

  // Helper function to play click sound
  const playClickSound = () => {
    if (soundCtx && soundCtx.playSoundFx) soundCtx.playSoundFx('click');
  };

  // Play click SFX on any modal button click
  const handleAction = async (action) => {
    // Resume AudioContext for immediate sound playback
    if (soundCtx && soundCtx.ensureAudioContextResumed) {
      await soundCtx.ensureAudioContextResumed();
    }
    if (soundCtx && soundCtx.playSoundFx) soundCtx.playSoundFx('click');
    
    switch (action) {
      case 'resetVolumes':
        if (soundCtx) {
          soundCtx.setFilterVolume(50);
          soundCtx.setPumpVolume(50);
          soundCtx.setSoundFxVolume(50);
          if (soundCtx.isMuted) {
            if (typeof soundCtx.setMute === 'function') {
              soundCtx.setMute(false);
            } else if (typeof soundCtx.toggleMute === 'function') {
              soundCtx.toggleMute(); // fallback if setMute not available
            }
          }
        }
        break;
      case 'close':
        closeModal();
        break;
      case 'goKillModal':
        openModal(MODAL_TYPES.KILL);
        break;
      case 'goVibe':
        closeModal();
        if (goToVibe) goToVibe();
        break;
      case 'goIntro':
        openModal(MODAL_TYPES.INTRO);
        break;
      case 'goKillSimulator':
        closeModal();
        if (goToKill) goToKill();
        break;
      case 'goBackToSimulator':
        closeModal();
        if (lastSimulator === 'vibe' && goToVibe) goToVibe();
        else if (lastSimulator === 'kill' && goToKill) goToKill();
        else if (goToIntro) goToIntro();
        break;
      case 'goLabbyLabs':
        trackEvent('external_link', { destination: 'labby_labs', source: 'about_modal_button' });
        window.open('https://labbylabs.xyz', '_blank');
        break;
      case 'goDonateOceanicSociety':
        trackEvent('external_link', { destination: 'oceanic_society', source: 'about_modal_button' });
        window.open('https://thegivingblock.com/donate/oceanic-society/', '_blank');
        break;
      default:
        closeModal();
    }
  };

  return (
    <div 
      className="modal" 
      style={{ 
        overflowY: 'visible',
        overflowX: 'hidden', // Prevent horizontal scroll
        maxWidth: '100vw', // Prevent overflow on mobile
        // Prevent CLS by using visibility instead of removing from DOM
        visibility: (modal.isOpen && modalConfig[modal.type]) ? 'visible' : 'hidden',
        opacity: (modal.isOpen && modalConfig[modal.type]) ? 1 : 0,
        // Add transition for smooth appearance
        transition: 'opacity 0.15s ease-out, visibility 0.15s ease-out',
        // Prevent interaction when hidden
        pointerEvents: (modal.isOpen && modalConfig[modal.type]) ? 'auto' : 'none',
      }}
    >
      {/* Only render content when modal is supposed to be visible and has valid config */}
      {(modal.isOpen && config) && (
        <>
          {config.showLogo !== false && <img src={logoImage} alt="Logo" className="modal-logo" />}
          {config.showTitle !== false && (
            <h1
              className="modal-header-title"
              style={{ cursor: 'inherit' }}
            >
              {config.title}
            </h1>
          )}
        </>
      )}
      <style>{`
        .modal-header-title:hover {
          cursor: text !important;
        }
        .modal-content-scrollable p:hover {
          cursor: text !important;
        }
        .modal-content-scrollable p a:hover,
        .modal-content-scrollable p button:hover,
        .modal-content-scrollable p span[role="button"]:hover {
          cursor: pointer !important;
        }
      `}</style>
      <div
        className="modal-content-scrollable"
        style={{
          maxHeight: '32vh',
          overflowY: 'auto',
          width: '100%',
          marginBottom: '0.5em',
        }}
      >
        {(modal.type === 'SoundSettings' || modal.type === MODAL_TYPES.SOUND_SETTINGS) ? (
          <ErrorBoundary>
            <SoundSettingsContent />
          </ErrorBoundary>
        ) : (
          <div style={{ width: '100%' }}>
            <style>{`
              .modal-content-scrollable p {
                display: inline-block;
                margin-left: auto;
                margin-right: auto;
                text-align: center;
                width: 100%;
              }
              .about-main-desc {
                margin-bottom: 0.5em !important;
              }
              .wisdom-quote-container {
                margin: 2.5em 0 1.5em 0;
                padding: 0;
                border: none;
                text-align: center;
                width: 100%;
              }
              .wisdom-quote {
                color: #fffbe8;
                font-style: italic;
                text-align: center;
                line-height: 1.6;
                margin: 0 0 1em 0;
                width: 100%;
                display: block;
              }
              .wisdom-attribution {
                color: #bcbcbc;
                font-style: normal;
                font-size: 0.85em;
                text-align: center;
                margin: 0;
                display: block;
                width: 100%;
              }
            `}</style>
            {typeof config.content === 'function'
              ? config.content({
                  ...modal.props,
                  handleLabbyLabsClick: () => handleAction('goLabbyLabs'),
                  showNewsletterForm,
                  setShowNewsletterForm,
                  showCharityInfo,
                  setShowCharityInfo,
                  playClickSound,
                  wisdomQuote,
                  setWisdomQuote: () => {}, // no-op, legacy prop for compatibility
                })
              : config.content}
          </div>
        )}
      </div>
      <div className="modal-buttons" style={{ marginTop: '0.1em' }}>
        {config.buttons && config.buttons.map((btn, idx) => (
          <button 
            key={idx} 
            onClick={() => handleAction(btn.action)}
            className={btn.primary ? 'primary-button' : 'secondary-button'}
            style={{ fontWeight: 600 }}
          >
            {btn.label}
          </button>
        ))}
        {/* Mobile-only style for secondary (left) modal button text color */}
        <style>{`
          @media (hover: none) and (pointer: coarse) {
            .modal-buttons .secondary-button {
              color: #222 !important;
            }
          }
        `}</style>
      </div>
      {/* Subtle disclaimer for Intro modal */}
      {modal.type === MODAL_TYPES.INTRO && (
        <div
          className="modal-intro-footer"
          style={{
            marginTop: '1.5em',
            fontSize: '0.7rem',
            color: '#888',
            textAlign: 'center',
            width: '100%',
            lineHeight: 1.4,
          }}
        >
          This site is a satirical art experience and is not affiliated with any adult content platforms
        </div>
      )}
      <style>{`
        .modal-header-title:hover {
          cursor: text !important;
        }
        .modal-content-scrollable p:hover {
          cursor: text !important;
        }
        .modal-content-scrollable p a:hover,
        .modal-content-scrollable p button:hover,
        .modal-content-scrollable p span[role="button"]:hover {
          cursor: pointer !important;
        }
        .modal-intro-footer:hover {
          cursor: text !important;
        }
        .modal-intro-footer a:hover,
        .modal-intro-footer button:hover,
        .modal-intro-footer span[role="button"]:hover {
          cursor: pointer !important;
        }
      `}</style>

      {/* Subtle legal footer for About modal with expandable disclaimers */}
      {modal.type === MODAL_TYPES.ABOUT && (
        showDisclaimers ? (
          <div
            style={{
              marginTop: '1.5em',
              fontSize: '0.7rem',
              color: '#888',
              textAlign: 'center',
              width: '100%',
              lineHeight: 1.5,
              marginBottom: '0.2em',
              wordBreak: 'break-word',
              paddingLeft: 8,
              paddingRight: 8,
            }}
          >
            <style>{`
              .about-disclaimer-line:hover { cursor: text !important; }
              .about-disclaimers-close:hover { cursor: pointer !important; color: #2596be !important; }
            `}</style>
            <div className="about-disclaimer-line">PrawnHub is a non-commercial, satirical art experience</div>
            <div className="about-disclaimer-line">PrawnHub is not affiliated with any adult content platforms or with the Oceanic Society</div>
            <div className="about-disclaimer-line">All trademarks, images, and sounds remain the property of their respective owners</div>
            <div className="about-disclaimer-line">This site is operated by Labby Labs LLC, a Colorado limited liability company</div>
            <div className="about-disclaimer-line">If you are a rights holder with concerns, please contact sup@labbylabs.xyz</div>
            <div
              className="about-disclaimers-close"
              style={{
                marginTop: '0.2em',
                fontSize: '0.7rem',
                color: '#999',
                textAlign: 'center',
                width: '100%',
                cursor: 'pointer',
                textDecoration: 'underline',
                textUnderlineOffset: 3,
                userSelect: 'none',
                transition: 'color 0.15s',
              }}
              onClick={() => {
                if (playClickSound) playClickSound();
                setShowDisclaimers(false);
              }}
              onKeyDown={e => { 
                if (e.key === 'Enter' || e.key === ' ') {
                  if (playClickSound) playClickSound();
                  setShowDisclaimers(false);
                }
              }}
              tabIndex={0}
              role="button"
            >
              Close
            </div>
          </div>
        ) : (
          <div
            className="about-disclaimers-toggle"
            style={{
              marginTop: '1.5em',
              fontSize: '0.7rem',
              color: '#999',
              textAlign: 'center',
              width: '100%',
              lineHeight: 1.5,
              marginBottom: '0.2em',
              cursor: 'pointer',
              textDecoration: 'underline',
              textUnderlineOffset: 3,
              userSelect: 'none',
              paddingLeft: 8,
              paddingRight: 8,
              transition: 'color 0.15s',
            }}
            onClick={() => {
              if (playClickSound) playClickSound();
              setShowDisclaimers(true);
            }}
            onKeyDown={e => { 
              if (e.key === 'Enter' || e.key === ' ') {
                if (playClickSound) playClickSound();
                setShowDisclaimers(true);
              }
            }}
            tabIndex={0}
            role="button"
          >
            <style>{`.about-disclaimers-toggle:hover { cursor: pointer !important; color: #2596be !important; }`}</style>
            Disclaimers
          </div>
        )
      )}
    </div>
  );
};

export default Modal;